import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../authentication/auth.service';
import { throwError, BehaviorSubject, Observable, of, timer } from 'rxjs';
import { catchError, filter, switchMap, take, retry, mergeMap, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Tokens } from '@app/core/models/core-models';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationTypes } from '../helpers/constants';
const HTTP_BAD_REQUEST_CODE = 400;
const HTTP_BAD_REQUEST_UNAUTHORIZED_CODE = 401;
const HTTP_BAD_REQUEST_FORBIDDEN_CODE = 403;
const MAX_REFRESH_ATTEMPTS = 3;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Tracks if a token refresh is in progress
  private isRefreshing = false;

  // Stores the refreshed token to synchronize multiple HTTP requests
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  // Licznik prób odświeżenia tokenu
  private refreshAttempts = 0;

  // Flaga i timer dla obsługi password_change_required
  private isHandlingPasswordChangeRequired = false;
  private passwordChangeRequiredTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private globalNotificationsService: GlobalNotificationsService
  ) {}

  /**
   * Intercepts HTTP requests to handle token injection and error handling.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Debug log do śledzenia życia interceptora
    this.logDebug(`🔍 Intercepting request to: ${request.url}`);

    // Ignore auth endpoints
    if (this.isAuthEndpoint(request.url)) {
      return next.handle(request);
    }

    const accessToken = this.authService.getAccessToken();

    // Check if we need to refresh the token proactively before making the request
    if (accessToken && !this.authService.isTokenValid() && !this.isRefreshing) {
      this.logDebug(`🔄 Token expired, refreshing before request to: ${request.url}`);
      return this.refreshAndRetryRequest(request, next);
    }

    // Attach token to the request if available
    if (accessToken) {
      request = this.addToken(request, accessToken);
    }

    return next.handle(request).pipe(
      catchError((response: HttpErrorResponse) => {
        const { status } = response;

        this.logDebug(`🚫 Request error: ${status} for ${request.url}`);

        // Ignore errors from login endpoint
        if (this.isAuthEndpoint(request.url)) {
          return throwError(response);
        }

        // If not a 401 or 400 error, let it pass through
        if (![HTTP_BAD_REQUEST_CODE, HTTP_BAD_REQUEST_UNAUTHORIZED_CODE, HTTP_BAD_REQUEST_FORBIDDEN_CODE].includes(status)) {
          return throwError(response);
        }

        // Handle token refresh logic for 401 Unauthorized errors
        if (status === HTTP_BAD_REQUEST_UNAUTHORIZED_CODE) {
          return this.handle401Error(request, next);
        }

        if (status === HTTP_BAD_REQUEST_FORBIDDEN_CODE) {
          return this.handle403Error(response);
        }

        // Handle other errors
        return throwError(response);
      })
    );
  }

  /**
   * Checks if a request is targeting an authentication-related endpoint.
   */
  private isAuthEndpoint(url: string): boolean {
    return url.includes('/auth/login') || url.includes('/auth/refresh');
  }

  /**
   * Adds the Authorization header to the request.
   */
  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json'
      }
    });
  }

  /**
   * Handles 401 errors by attempting to refresh the token and retrying the request.
   */

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Dodajemy sprawdzenie czy użytkownik ma refresh token
    if (!this.authService.getRefreshToken()) {
      this.logDebug('❌ No refresh token available, redirecting to login');
      this.redirectToLogin();
      return throwError(new Error('No refresh token'));
    }

    if (this.isRefreshing) {
      this.logDebug('⏳ Token refresh already in progress, queuing request');
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          // Dodajemy sprawdzenie czy token istnieje
          if (!token) {
            this.logDebug('❌ Token refresh failed, redirecting to login');
            this.redirectToLogin();
            return throwError(new Error('Token refresh failed'));
          }
          this.logDebug(`✅ Using refreshed token for request to ${request.url}`);
          return next.handle(this.addToken(request, token));
        })
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);
    this.logDebug('🔄 Starting token refresh');

    return this.refreshWithRetry().pipe(
      switchMap((tokens: Tokens) => {
        const newToken = tokens.access_token;
        this.isRefreshing = false;
        this.refreshAttempts = 0; // Reset attempts count
        this.refreshTokenSubject.next(newToken);
        this.logDebug(`✅ Token refreshed successfully, retrying request to ${request.url}`);
        return next.handle(this.addToken(request, newToken));
      }),
      catchError(error => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        this.logDebug(`❌ Token refresh failed with error: ${error.message || 'Unknown error'}`);

        // Dodajemy sprawdzenie typu błędu
        if (error.status === 401 || error.status === 403 || error instanceof Error) {
          this.redirectToLogin();
        }
        return throwError(error);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  private handle403Error(response: HttpErrorResponse): Observable<any> {
    if (response.error.error === 'password_change_required') {
      // Sprawdzamy czy już obsługujemy ten błąd
      if (this.isHandlingPasswordChangeRequired) {
        this.logDebug('⏳ Password change required already being handled, skipping');
        return throwError(new Error(response.error.message));
      }

      // Ustawiamy flagę i wykonujemy akcje
      this.isHandlingPasswordChangeRequired = true;
      this.logDebug('🔐 Handling password change required');

      this.globalNotificationsService.show({
        message: response.error.message,
        type: NotificationTypes[1],
        id: ''
      });

      this.router.navigate(['auth/password-change-required']);

      // Resetujemy flagę po 2 sekundach
      this.passwordChangeRequiredTimer = setTimeout(() => {
        this.isHandlingPasswordChangeRequired = false;
        this.passwordChangeRequiredTimer = null;
        this.logDebug('🔐 Password change required flag reset');
      }, 2000);
    } else {
      // Dla innych błędów 403 pokazujemy notyfikację
      if (!response.url?.includes('configuration/list')) {
        this.globalNotificationsService.show({
          message: response.error.errors.message,
          type: NotificationTypes[1],
          id: ''
        });
      }
    }

    return throwError(new Error(response.error.message));
  }
  /**
   * Attempts to refresh the token with retry capability
   */
  private refreshWithRetry(): Observable<Tokens> {
    return this.authService.tokenRefresh().pipe(
      retry(2), // Retry up to 2 additional times
      catchError(error => {
        this.logDebug(`❌ All token refresh attempts failed: ${error.message}`);
        return throwError(error);
      })
    );
  }

  /**
   * Redirects the user to the login page with the return URL.
   */
  private redirectToLogin(): void {
    const currentUrl = this.router.url;
    this.logDebug(`🔀 Redirecting to login page, return URL: ${currentUrl}`);
    this.clearPasswordChangeRequiredTimer();
    this.authService.doLogoutUser();
    this.router.navigate(['auth/login'], { queryParams: { returnUrl: currentUrl } });
  }

  /**
   * Czyści timer dla obsługi password_change_required
   */
  private clearPasswordChangeRequiredTimer(): void {
    if (this.passwordChangeRequiredTimer) {
      clearTimeout(this.passwordChangeRequiredTimer);
      this.passwordChangeRequiredTimer = null;
      this.isHandlingPasswordChangeRequired = false;
      this.logDebug('🔐 Password change required timer cleared');
    }
  }

  /**
   * Loguje wiadomości debugowania
   */
  private logDebug(message: string): void {
    // console.debug(`[AuthInterceptor] ${message}`);
  }

  /**
   * Refreshes the token and retries the original request
   */
  private refreshAndRetryRequest(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.refreshWithRetry().pipe(
      switchMap((tokens: Tokens) => {
        const newToken = tokens.access_token;
        this.isRefreshing = false;
        this.refreshTokenSubject.next(newToken);
        this.logDebug('✅ Token refreshed proactively, continuing with request');
        return next.handle(this.addToken(request, newToken));
      }),
      catchError(error => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        this.logDebug(`❌ Proactive token refresh failed: ${error.message}`);

        if (error.status === 401 || error.status === 403) {
          this.redirectToLogin();
        }

        return throwError(error);
      })
    );
  }
}
